import React, { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useCloudSecurity } from '../context/CloudSecurityContext';
import { getAllFrameworks } from '../data/frameworkMappings';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  display: ${props => props.visible ? 'block' : 'none'};
  margin-top: 40px;
  text-align: center;
  animation: ${fadeIn} 0.5s ease;
`;

const OverallScoreContainer = styled.div`
  background: linear-gradient(135deg, #1a1f3a 0%, #2d3561 100%);
  border-radius: 20px;
  padding: 40px;
  margin-bottom: 30px;
  border: 2px solid #667eea;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.3);
`;

const ScoreCircle = styled.div`
  width: 200px;
  height: 200px;
  margin: 0 auto 20px;
  position: relative;
`;

const ScoreValue = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 3em;
  font-weight: bold;
`;

const ScoreDescription = styled.p`
  margin-top: 20px;
  font-size: 1.2em;
`;

const FrameworkBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
`;

const FrameworkBadge = styled.div`
  padding: 6px 12px;
  background: rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.3);
  border-radius: 20px;
  font-size: 0.85em;
  color: #e0e6ed;
`;

const RecommendationsContainer = styled.div`
  background: #1a1f3a;
  border-radius: 16px;
  padding: 30px;
  text-align: left;
  border: 1px solid #2d3561;
`;

const RecommendationTitle = styled.h3`
  margin-bottom: 20px;
`;

const RecommendationItem = styled.div`
  background: #0a0e27;
  padding: 15px;
  margin-bottom: 15px;
  border-radius: 10px;
  border-left: 4px solid #f43f5e;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateX(5px);
    box-shadow: 0 5px 15px rgba(244, 63, 94, 0.2);
  }
`;

const RecommendationText = styled.strong`
  display: block;
  margin-bottom: 5px;
`;

const RecommendationDescription = styled.p`
  margin-top: 5px;
  color: #8892b0;
`;

const GenerateReportButton = styled.button`
  margin-top: 30px;
  padding: 18px 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.1em;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 5px 20px rgba(102, 126, 234, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(102, 126, 234, 0.5);
  }
`;

const BackButton = styled.button`
  margin-top: 20px;
  padding: 12px 30px;
  background: transparent;
  color: #667eea;
  border: 2px solid #667eea;
  border-radius: 12px;
  font-size: 1em;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(102, 126, 234, 0.1);
    transform: translateY(-2px);
  }
`;

const Results = () => {
  const { 
    currentProvider,
    showResults,
    calculateOverallScore,
    generateRecommendations,
    generateReport,
    setShowResults,
    selectedFramework
  } = useCloudSecurity();
  
  const circleRef = useRef(null);
  const scoreValueRef = useRef(null);
  
  const { percentage } = calculateOverallScore();
  const recommendations = generateRecommendations().slice(0, 5);
  
  // Get all frameworks considered in the assessment
  const frameworks = selectedFramework === 'All' 
    ? getAllFrameworks() 
    : [selectedFramework];
  
  useEffect(() => {
    if (showResults && circleRef.current && scoreValueRef.current) {
      const circle = circleRef.current;
      const scoreValue = scoreValueRef.current;
      
      // Calculate circle properties
      const radius = 90;
      const circumference = 2 * Math.PI * radius;
      const offset = circumference - (percentage / 100) * circumference;
      
      // Set circle properties
      circle.style.strokeDasharray = `${circumference} ${circumference}`;
      circle.style.strokeDashoffset = circumference;
      
      // Animate the circle
      setTimeout(() => {
        circle.style.transition = 'stroke-dashoffset 1.5s ease-in-out';
        circle.style.strokeDashoffset = offset;
      }, 100);
      
      // Animate the score value
      let currentValue = 0;
      const increment = percentage / 50;
      const timer = setInterval(() => {
        currentValue += increment;
        if (currentValue >= percentage) {
          currentValue = percentage;
          clearInterval(timer);
        }
        scoreValue.textContent = Math.round(currentValue) + '%';
      }, 30);
      
      return () => clearInterval(timer);
    }
  }, [showResults, percentage]);
  
  const handleGenerateReport = () => {
    const reportText = generateReport();
    
    // Create and download the report
    const blob = new Blob([reportText], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cloud-security-assessment-${currentProvider}-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };
  
  // Get score description based on percentage
  const getScoreDescription = () => {
    if (percentage >= 90) {
      return '🌟 Excellent! Your cloud security posture is very strong.';
    } else if (percentage >= 70) {
      return '👍 Good job! Some improvements needed for optimal security.';
    } else if (percentage >= 50) {
      return '⚠️ Fair. Significant security improvements recommended.';
    } else {
      return '🚨 Critical! Immediate security remediation required.';
    }
  };
  
  if (!currentProvider || !showResults) {
    return null;
  }
  
  return (
    <Container visible={showResults}>
      <OverallScoreContainer>
        <h2>Overall Security Score</h2>
        <ScoreCircle>
          <svg width="200" height="200">
            <circle 
              cx="100" 
              cy="100" 
              r="90" 
              fill="none" 
              stroke="#2d3561" 
              strokeWidth="20"
            />
            <circle 
              ref={circleRef}
              cx="100" 
              cy="100" 
              r="90" 
              fill="none" 
              stroke="url(#gradient)" 
              strokeWidth="20" 
              strokeDasharray="565.48" 
              strokeDashoffset="565.48" 
              transform="rotate(-90 100 100)"
            />
            <defs>
              <linearGradient id="gradient">
                <stop offset="0%" stopColor="#667eea"/>
                <stop offset="100%" stopColor="#764ba2"/>
              </linearGradient>
            </defs>
          </svg>
          <ScoreValue ref={scoreValueRef}>0%</ScoreValue>
        </ScoreCircle>
        <ScoreDescription>{getScoreDescription()}</ScoreDescription>
        
        <FrameworkBadges>
          {frameworks.map(framework => (
            <FrameworkBadge key={framework}>
              {framework}
            </FrameworkBadge>
          ))}
        </FrameworkBadges>
      </OverallScoreContainer>
      
      <RecommendationsContainer>
        <RecommendationTitle>🎯 Priority Recommendations</RecommendationTitle>
        
        {recommendations.length > 0 ? (
          recommendations.map((rec, index) => (
            <RecommendationItem key={index}>
              <RecommendationText>
                {rec.category}: {rec.check}
              </RecommendationText>
              <RecommendationDescription>
                {rec.description}
              </RecommendationDescription>
            </RecommendationItem>
          ))
        ) : (
          <p style={{ color: '#10b981' }}>
            ✅ All high-risk items are addressed! Review medium and low risk items for additional hardening.
          </p>
        )}
      </RecommendationsContainer>
      
      <GenerateReportButton onClick={handleGenerateReport}>
        📄 Generate Detailed Report
      </GenerateReportButton>
      
      <div>
        <BackButton onClick={() => setShowResults(false)}>
          ← Back to Assessment
        </BackButton>
      </div>
    </Container>
  );
};

export default Results; 